import React from "react";
import { SectionTitleText, SubduedText } from "@novel/shared/components/Primitives";
import { ThreeDotVertical, UpIcon } from "@novel/shared/icons";
import { IBenefits } from "@novel/shared/interfaces/Discounts";
import { ITokenWithMetadataAndCollection } from "@novel/shared/interfaces/Token";
import { ITokenMetadataAttribute } from "@novel/shared/interfaces/TokenMetadata";
import { toViewLongDate } from "@novel/shared/utils/dateToText";
import { Box } from "@novel/shared/components/Box";
import { TableCellImagesList } from "@novel/shared/components/Table/TableCellImagesList";
import { Col, Grid, Row } from "rsuite";
import { Button } from "@shopify/polaris";
import { IStorefrontGatePriceRule, IStorefrontGateProduct } from "@novel/shared/interfaces/StorefrontData";
import { openInNewTab } from "@novel/shared/utils/locationUtils";
import { IPFS_GATEWAY_PREFIX } from "@novel/shared/utils/appConstants";
import { appPrefixedHostWithProtocol } from "@novel/shared/utils/appDomainConstants";
interface IHeader {
  readonly title: string;
}
function StyledHeaderLabel({
  title
}: IHeader): JSX.Element {
  return <SectionTitleText className="subtext-heading-modal" style={{
    marginBottom: "0px"
  }}>
            {title}
        </SectionTitleText>;
}
function HeaderText({
  title
}: IHeader): JSX.Element {
  return <div className="table-header-smaple">
            <StyledHeaderLabel title={title} />
        </div>;
}
function ActionableHeaderText({
  title,
  displayTrigger,
  action
}: IHeader & {
  readonly displayTrigger: boolean;
  readonly action: () => void;
}): JSX.Element {
  return <div className="table-header-smaple">
            <StyledHeaderLabel title={title} />
            {displayTrigger && <Button plain onClick={action}>
                    View All
                </Button>}
        </div>;
}
export function AttributeList({
  attributeAll,
  attributeMore,
  claimedToken,
  ToggleAttribute
}: {
  readonly attributeAll?: ITokenMetadataAttribute[];
  readonly attributeMore: boolean;
  readonly claimedToken: ITokenWithMetadataAndCollection;
  readonly ToggleAttribute: () => void;
}): JSX.Element | null {
  if (!attributeAll || !attributeAll.length) return null;
  return <React.Fragment>
            <hr className="line-spacer-main" />
            <SectionTitleText className="subtext-heading-modal">Attributes</SectionTitleText>
            <div className="flex-block-switch">
                <Box columnGap={1} rowGap={1} flexWrap="wrap" mb={2} className="attribute-box-area">
                    {attributeAll.map((attribute, index) => {
          // TODO: should we be converting "trait_type" to "attributeName" somewhere in our back end?
          const attributeName = attribute.attributeName ? attribute.attributeName.toUpperCase() : (attribute as {
            trait_type?: string;
          }).trait_type?.toUpperCase() || "PROPERTY";

          // TODO: should we be converting "display_type" to "displayType" somewhere in our back end?
          const displayType = attribute.displayType || (attribute as {
            display_type?: string;
          }).display_type || "text";
          const displayValue = displayType === "date" && attribute.value ? toViewLongDate(new Date(attribute.value), true) : attribute.value;
          return <React.Fragment key={index}>
                                {Boolean(displayValue) && <Box className="token-attribute" column rowGap={0.5}>
                                        <SubduedText className="trait_name">
                                            {attributeName}
                                        </SubduedText>
                                        <div className="trait_value">{displayValue}</div>
                                    </Box>}
                            </React.Fragment>;
        })}
                </Box>
                {claimedToken.tokenMetadata?.attributes && claimedToken.tokenMetadata?.attributes.length > 6 && <button className="grey-button-see-more" onClick={ToggleAttribute}>
                            {attributeMore ? "Less" : "More"}
                        </button>}
                <div className="attributeMore" style={{
        justifyContent: `${attributeMore ? "flex-end" : "flex-start"}`
      }}>
                    {claimedToken.tokenMetadata?.attributes && claimedToken.tokenMetadata?.attributes.length > 4 && <div className="button-readmore" onClick={ToggleAttribute}>
                                {attributeMore ? <UpIcon /> : <ThreeDotVertical />}
                            </div>}
                </div>
            </div>
        </React.Fragment>;
}
export function ProductsList({
  productAll,
  filteredNFTItem,
  productMore,
  ToggleProduct
}: {
  readonly productAll?: IStorefrontGateProduct[];
  readonly filteredNFTItem?: IBenefits;
  readonly productMore: boolean;
  readonly ToggleProduct: () => void;
}): JSX.Element | null {
  if (!productAll || !productAll.length) return null;
  const products = filteredNFTItem?.products || [];
  return <div className="box-container-section">
            <div className="box-section-area-sm">
                <HeaderText title="Gated Products" />
                <div className="benefit-content-products">
                    {productAll.map(product => <a href={product.pageUrl} target="_blank" key={product.externalId}>
                            <div className="product-box">
                                <TableCellImagesList imageUrls={product?.imageUrl ? [product.imageUrl] : []} />
                                <div className="product-name-benefit">{product.title}</div>
                            </div>
                        </a>)}
                </div>
            </div>
            {products.length > 6 && <button className="grey-button-see-more" onClick={ToggleProduct}>
                    {productMore ? "Less" : "More"}
                </button>}
        </div>;
}
export function DiscountList({
  discountsAll,
  filteredNFTItem,
  discountMore,
  ToggleDiscount
}: {
  readonly discountsAll?: IStorefrontGatePriceRule[];
  readonly filteredNFTItem?: IBenefits;
  readonly discountMore: boolean;
  readonly ToggleDiscount: () => void;
}): JSX.Element | null {
  if (!discountsAll || !discountsAll.length) return null;
  return <div className="box-container-section">
            <div className="box-section-area-sm">
                <HeaderText title="Gated Discounts" />
                <div className="benefit-content-urls">
                    {discountsAll.map(discount => {
          const {
            homepageUrl,
            entitlementProducts,
            entitlementCollections
          } = discount;

          // use specific product page, fall back to collection page, fall back to homepage
          const discountLink = entitlementCollections?.length && entitlementCollections[0]?.collectionPageUrl || entitlementProducts?.length && entitlementProducts[0]?.pageUrl || homepageUrl;
          return <div className="url-gated-list-area-sm" key={discount.id}>
                                <a href={discountLink} target="_blank">
                                    <span className="discount-idea-name-sm">
                                        • {discount.summary}
                                    </span>
                                </a>
                            </div>;
        })}
                </div>
            </div>
            {filteredNFTItem?.discounts && filteredNFTItem?.discounts.length > 2 && <button className="grey-button-see-more" onClick={ToggleDiscount}>
                    {discountMore ? "Less" : "More"}
                </button>}
        </div>;
}
export function GatedUrlList({
  gatedUrls,
  filteredNFTItem,
  urlsMore,
  ToggleUrls
}: {
  readonly gatedUrls?: string[];
  readonly filteredNFTItem?: IBenefits;
  readonly urlsMore: boolean;
  readonly ToggleUrls: () => void;
}): JSX.Element | null {
  if (!gatedUrls || !gatedUrls.length) return null;
  return <div className="box-container-section">
            <div className="box-section-area-sm">
                <HeaderText title="Gated Urls" />
                <div className="benefit-content-urls">
                    {gatedUrls.map(url => <div className="url-gated-list-area" key={url}>
                            <a href={url} target="_blank">
                                <span className="url-idea-name">• {url}</span>
                            </a>
                        </div>)}
                </div>
            </div>
            {filteredNFTItem?.gatedUrls && filteredNFTItem?.gatedUrls.length > 2 && <button className="grey-button-see-more" onClick={ToggleUrls}>
                    {urlsMore ? "Less" : "More"}
                </button>}
        </div>;
}
interface IPageList {
  readonly filteredNFTItem?: IBenefits;
  readonly TogglePage: (page: string) => void;
}
export function PageProductsList({
  filteredNFTItem,
  TogglePage
}: IPageList): JSX.Element | null {
  if (!filteredNFTItem) return null;
  const {
    products
  } = filteredNFTItem;
  if (!products || !products.length) return null;
  return <Col xs={24} className="box-area-benefits">
            <ActionableHeaderText title="Gated Products" displayTrigger={products.length > 4} action={() => TogglePage("PRODUCT")} />
            <div className="benefit-content-products">
                {products.slice(0, 7).map(product => <a href={product.pageUrl} target="_blank" key={product.externalId}>
                        <div className="product-box">
                            <TableCellImagesList imageUrls={product?.imageUrl ? [product.imageUrl] : []} />
                            <div className="product-name-benefit">{product.title}</div>
                        </div>
                    </a>)}
            </div>
        </Col>;
}
export function PageGatedUrlsList({
  filteredNFTItem,
  TogglePage
}: IPageList): JSX.Element | null {
  if (!filteredNFTItem) return null;
  const {
    products,
    gatedUrls,
    discounts
  } = filteredNFTItem;
  if (!gatedUrls || !gatedUrls.length) return null;
  return <Col xs={products && products?.length === 0 || discounts && discounts.length === 0 ? 24 : 10} className="box-area-benefits">
            <ActionableHeaderText title="Gated Urls" displayTrigger={gatedUrls.length > 4} action={() => TogglePage("URL")} />
            <div className="benefit-content-urls">
                {gatedUrls.slice(0, 3).map(url => <div className="url-gated-list-area" key={url}>
                        <a href={url} target="_blank">
                            <span className="url-idea-name">• {url}</span>
                        </a>
                    </div>)}
            </div>
        </Col>;
}
export function PageDiscountList({
  filteredNFTItem,
  TogglePage
}: IPageList): JSX.Element | null {
  if (!filteredNFTItem) return null;
  const {
    products,
    gatedUrls,
    discounts
  } = filteredNFTItem;
  if (!discounts || !discounts.length) return null;
  return <Col xs={products && products?.length === 0 || gatedUrls && gatedUrls.length === 0 ? 24 : 14} className="box-area-benefits">
            <ActionableHeaderText title="Gated Discounts" displayTrigger={discounts.length > 4} action={() => TogglePage("DISCOUNT")} />
            <div className="benefit-content-urls">
                {discounts.slice(0, 3).map(discount => {
        const {
          homepageUrl,
          entitlementProducts,
          entitlementCollections
        } = discount;

        // use specific product page, fall back to collection page, fall back to homepage
        const discountLink = entitlementCollections?.length && entitlementCollections[0]?.collectionPageUrl || entitlementProducts?.length && entitlementProducts[0]?.pageUrl || homepageUrl;
        return <div className="url-gated-list-area" key={discount.id}>
                            <a href={discountLink} target="_blank">
                                <span className="discount-idea-name">• {discount.summary}</span>
                            </a>
                        </div>;
      })}
            </div>
        </Col>;
}
export function PageBenefitList({
  TogglePage
}: IPageList): JSX.Element | null {
  return <Col xs={24} className="box-area-benefits">
            <ActionableHeaderText title="Benefits" displayTrigger action={() => TogglePage("ALL")} />
            <div className="benefit-content-text" />
        </Col>;
}
export function PageProductsTableList({
  filteredNFTItem,
  TogglePage
}: IPageList): JSX.Element | null {
  if (!filteredNFTItem) return null;
  const {
    products
  } = filteredNFTItem;
  if (!products || !products.length) return null;
  return <Col xs={24} className="box-area-benefits">
            <ActionableHeaderText title="Gated Products" displayTrigger action={() => TogglePage("ALL")} />
            <div className="benefit-content-products">
                {products.map(product => <div className="product-box" key={product.externalId}>
                        <TableCellImagesList imageUrls={product?.imageUrl ? [product.imageUrl] : []} />
                        <div className="product-name-benefit">{product.title}</div>
                    </div>)}
            </div>
        </Col>;
}
export function PageUrlsList({
  filteredNFTItem,
  TogglePage
}: IPageList): JSX.Element | null {
  if (!filteredNFTItem) return null;
  const {
    gatedUrls
  } = filteredNFTItem;
  if (!gatedUrls || !gatedUrls.length) return null;
  return <Col xs={24} className="box-area-benefits">
            <ActionableHeaderText title="Gated Urls" displayTrigger action={() => TogglePage("ALL")} />
            <div className="benefit-content-urls">
                <Grid fluid>
                    <Row className="show-grid">
                        {gatedUrls.map(url => <Col xs={12} className="url-gated-list-area box-col-spacing" key={url}>
                                <a href={url} target="_blank">
                                    <span className="url-idea-name">• {url}</span>
                                </a>
                            </Col>)}
                    </Row>
                </Grid>
            </div>
        </Col>;
}
export function PageDiscountAllList({
  filteredNFTItem,
  TogglePage
}: IPageList): JSX.Element | null {
  if (!filteredNFTItem) return null;
  const {
    discounts
  } = filteredNFTItem;
  if (!discounts || !discounts.length) return null;
  return <Col xs={24} className="box-area-benefits">
            <ActionableHeaderText title="Gated Discounts" displayTrigger action={() => TogglePage("ALL")} />
            <div className="benefit-content-urls">
                <Grid fluid>
                    <Row className="show-grid">
                        {discounts.map(discount => {
            const {
              homepageUrl,
              entitlementProducts,
              entitlementCollections
            } = discount;

            // use specific product page, fall back to collection page, fall back to homepage
            const discountLink = entitlementCollections?.length && entitlementCollections[0]?.collectionPageUrl || entitlementProducts?.length && entitlementProducts[0]?.pageUrl || homepageUrl;
            return <Col xs={12} className="url-gated-list-area box-col-spacing" key={discount.id}>
                                    <a href={discountLink} target="_blank">
                                        <span className="discount-idea-name">
                                            • {discount.summary}
                                        </span>
                                    </a>
                                </Col>;
          })}
                    </Row>
                </Grid>
            </div>
        </Col>;
}
interface LinksType {
  readonly claimedToken: ITokenWithMetadataAndCollection;
  readonly IPFSMetadata: string | undefined;
  readonly isUnrevealed: boolean;
}
export function Links({
  claimedToken,
  isUnrevealed,
  IPFSMetadata
}: LinksType): JSX.Element {
  return <React.Fragment>
            {claimedToken?.tokenMetadata && claimedToken?.tokenMetadata.ipfsImageCid && <button onClick={() => openInNewTab(claimedToken.tokenMetadata!.ipfsImageCid?.startsWith("https") ? `${claimedToken.tokenMetadata!.ipfsImageCid}` : `${IPFS_GATEWAY_PREFIX}${claimedToken.tokenMetadata!.ipfsImageCid}`)} className="grey-button">
                    IPFS Image
                </button>}
            {claimedToken.tokenMetadata && claimedToken.tokenMetadata.ipfsImageCid && IPFSMetadata && <button onClick={() => openInNewTab(IPFSMetadata)} className="grey-button">
                        IPFS Metadata
                    </button>}
            {claimedToken.collectionSmartContract.createdByNovel && !isUnrevealed && claimedToken.collectionSmartContract.createdByNovel && !isUnrevealed && <button onClick={() => openInNewTab(`${appPrefixedHostWithProtocol}/provenance/${claimedToken.collectionSmartContract.address}`)} className="grey-button" style={{
      width: "229px"
    }}>
                        COLLECTION PROVENANCE RECORD
                    </button>}
        </React.Fragment>;
}